import { Suspense } from 'react';
import type { ReactNode } from 'react';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { ErrorBoundary } from './error-boundary';

interface QueryBoundaryProps {
  fallback: ReactNode;
  /** Wraps the fallback and the error state, for children that render their own chrome (e.g. a ModalDialog). */
  renderFrame?: (content: ReactNode) => ReactNode;
  children: ReactNode;
  /** Return `undefined` to fall back to the generic error state, e.g. for status codes it does not handle. */
  renderError?: (error: unknown, retry: () => void) => ReactNode;
}

function QueryErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('common');
  return (
    <div role="alert" className="flex flex-col items-center gap-3 py-20 text-center">
      <p className="text-sm text-destructive">{t('apiErrors.generic')}</p>
      <Button size="sm" variant="outline" onClick={onRetry}>
        {t('error.retry')}
      </Button>
    </div>
  );
}

export function QueryBoundary({
  fallback,
  renderFrame = (content) => content,
  children,
  renderError,
}: QueryBoundaryProps) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          renderError={(retry, error) =>
            renderFrame(renderError?.(error, retry) ?? <QueryErrorState onRetry={retry} />)
          }
        >
          <Suspense fallback={renderFrame(fallback)}>{children}</Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
